import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ChevronRight, RefreshCw, CheckCircle2 } from 'lucide-react';
import type { GetPlanResponse, PlanItem } from '@claude-remote/shared';
import { api } from '../lib/api.js';

interface PlanPickerProps {
  projectId: string;
  /** Максимум пунктов для показа в quick actions (по умолчанию 3). */
  maxItems?: number;
}

/**
 * Компонент для показа незавершённых пунктов плана проекта.
 * При клике на пункт — переход на экран новой сессии с промптом.
 */
export function PlanPicker({ projectId, maxItems = 3 }: PlanPickerProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<PlanItem[]>([]);
  const [planPath, setPlanPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<GetPlanResponse>(`/api/v1/projects/${projectId}/plan`);
      if (res.plan) {
        setPlanPath(res.plan.path);
        // Показываем только незавершённые пункты.
        const pending = res.plan.items.filter((item) => !item.done);
        setItems(pending.slice(0, maxItems));
      } else {
        setPlanPath(null);
        setItems([]);
        if (res.error) setError(res.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки плана');
    } finally {
      setLoading(false);
    }
  }, [projectId, maxItems]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleItemClick(item: PlanItem) {
    // Переходим на экран новой сессии с промптом из плана.
    // Используем encodeURIComponent, т.к. в тексте могут быть спецсимволы.
    const prompt = encodeURIComponent(item.text);
    navigate(`/projects/${projectId}/new?prompt=${prompt}`);
  }

  // Ничего не показываем, пока загружаем или если нет плана.
  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-4">
        <div className="flex items-center gap-2 text-neutral-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Загружаем план…</span>
        </div>
      </div>
    );
  }

  if (error || items.length === 0) {
    // Не показываем ничего, если нет плана или ошибка — пользователь
    // всё равно может создать сессию вручную.
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-neutral-400 flex items-center gap-1.5">
          <FileText className="w-4 h-4" />
          Quick actions
          {planPath && (
            <span className="text-xs text-neutral-600 font-mono">({planPath})</span>
          )}
        </h2>
        <button
          onClick={() => void load()}
          className="p-1 text-neutral-500 hover:text-neutral-300"
          title="Обновить план"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.id}>
            <button
              onClick={() => handleItemClick(item)}
              className="w-full text-left rounded-xl border border-neutral-800 bg-neutral-900/60 p-3 hover:bg-neutral-800/60 active:bg-neutral-800 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate group-hover:text-blue-400 transition-colors">
                    {item.text}
                  </div>
                  {item.level > 0 && (
                    <div className="text-xs text-neutral-600">
                      {'·'.repeat(item.level)} уровень {item.level}
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-600 group-hover:text-neutral-400" />
              </div>
            </button>
          </li>
        ))}
      </ul>

      {items.length === maxItems && (
        <button
          onClick={() => navigate(`/projects/${projectId}/plan`)}
          className="w-full text-center text-xs text-neutral-500 hover:text-neutral-300 py-1"
        >
          Показать весь план →
        </button>
      )}
    </div>
  );
}

/**
 * Полный экран выбора пункта плана (если пользователь нажал «показать весь план»).
 */
export function PlanFullScreen({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<PlanItem[]>([]);
  const [planPath, setPlanPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<GetPlanResponse>(`/api/v1/projects/${projectId}/plan`);
      if (res.plan) {
        setPlanPath(res.plan.path);
        setItems(res.plan.items);
      } else {
        setPlanPath(null);
        setItems([]);
        if (res.error) setError(res.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки плана');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleItemClick(item: PlanItem) {
    const prompt = encodeURIComponent(item.text);
    navigate(`/projects/${projectId}/new?prompt=${prompt}`);
  }

  const filteredItems = showDone ? items : items.filter((i) => !i.done);
  const doneCount = items.filter((i) => i.done).length;
  const pendingCount = items.length - doneCount;

  if (loading) {
    return (
      <div className="p-8 text-center text-neutral-500">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        Загружаем план…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-400 mb-2">{error}</p>
        <button onClick={() => void load()} className="text-sm text-neutral-400 hover:text-white">
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">План проекта</h2>
          {planPath && (
            <p className="text-xs text-neutral-500 font-mono">{planPath}</p>
          )}
        </div>
        <div className="text-xs text-neutral-500">
          {pendingCount} осталось · {doneCount} готово
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setShowDone(false)}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            !showDone
              ? 'bg-blue-600/20 text-blue-400 border border-blue-600/40'
              : 'bg-neutral-800 text-neutral-400 border border-neutral-700'
          }`}
        >
          Незавершённые ({pendingCount})
        </button>
        <button
          onClick={() => setShowDone(true)}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            showDone
              ? 'bg-blue-600/20 text-blue-400 border border-blue-600/40'
              : 'bg-neutral-800 text-neutral-400 border border-neutral-700'
          }`}
        >
          Все ({items.length})
        </button>
      </div>

      <ul className="space-y-2">
        {filteredItems.map((item) => (
          <li key={item.id}>
            <button
              onClick={() => handleItemClick(item)}
              disabled={item.done}
              className={`w-full text-left rounded-xl border p-4 transition-colors group ${
                item.done
                  ? 'border-neutral-800/50 bg-neutral-900/30 opacity-60'
                  : 'border-neutral-800 bg-neutral-900/60 hover:bg-neutral-800/60 active:bg-neutral-800'
              }`}
            >
              <div className="flex items-start gap-3">
                {item.done ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-neutral-600 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div
                    className={`font-medium ${
                      item.done
                        ? 'line-through text-neutral-500'
                        : 'group-hover:text-blue-400'
                    }`}
                    style={{ paddingLeft: `${item.level * 0.75}rem` }}
                  >
                    {item.text}
                  </div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    Строка {item.line}
                  </div>
                </div>
                {!item.done && (
                  <ChevronRight className="w-5 h-5 text-neutral-600 group-hover:text-neutral-400 mt-0.5" />
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {filteredItems.length === 0 && (
        <p className="text-center text-neutral-500 py-8">
          {showDone ? 'План пуст' : 'Все пункты завершены!'}
        </p>
      )}
    </div>
  );
}
